import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, ShieldAlert, BellRing, Activity, AlertCircle, Search, Video,
  Stethoscope, MapPin, ChevronRight,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RISK_BADGE, READING_STATUS_BADGE, VITAL_SHORT, timeAgo } from "@/pages/clinical/constants";

function Kpi({ label, value, accent, icon: Icon, testId }) {
  return (
    <div className="hah-card p-3 flex items-start gap-3" data-testid={testId}>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}14`, color: accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="text-2xl font-bold text-[#1F2937]">{value ?? "—"}</div>
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#6B7280]">{label}</div>
      </div>
    </div>
  );
}

function RiskBadge({ ai, news2 }) {
  const level = ai || (news2 >= 5 ? "high" : news2 >= 3 ? "moderate" : "stable");
  const c = RISK_BADGE[level] || RISK_BADGE.null;
  return (
    <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider"
      style={{ background: c.bg, color: c.text }}>{c.label}</span>
  );
}

function AbnormalChip({ abn }) {
  if (!abn) return <span className="text-[10px] text-[#94A3B8]">—</span>;
  const c = READING_STATUS_BADGE[abn.status] || READING_STATUS_BADGE.amber;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold"
      style={{ background: c.bg, color: c.text }}>
      {VITAL_SHORT[abn.vital_type] || abn.vital_type} {abn.value}{abn.unit || ""} · {timeAgo(abn.taken_at)}
    </span>
  );
}

export default function ClinicalDashboard({ mode = "active" }) {
  // mode: "active" | "mine" | "high_risk"
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const [facilityId, setFacilityId] = useState(params.get("facility_id") || "all");
  const [facilities, setFacilities] = useState([]);

  const titleMap = {
    active: "Active H@H Patients",
    mine: "My Patients",
    high_risk: "High-Risk Patients",
  };
  const subtitleMap = {
    active: "Fully approved and activated patients in the Hospital @ Home program.",
    mine: "Patients where you are the attending physician or assigned nurse.",
    high_risk: "Patients with NEWS2 ≥ 5, AI deterioration risk ≥ high, or any open critical alert.",
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (q.trim()) p.q = q.trim();
      if (facilityId !== "all") p.facility_id = facilityId;
      if (mode === "mine") p.mine = true;
      if (mode === "high_risk") p.risk = "high";
      const r = await api.get("/clinical-ops/active-patients", { params: p });
      setItems(r.data.items || []);
      setKpis(r.data.kpis || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, facilityId, mode]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/facilities", { params: { page_size: 50 } }).then((r) => setFacilities(r.data.items || [])).catch(() => {});
  }, []);

  async function startTele(patientId) {
    try {
      const r = await api.post("/teleconsult/consultations", {
        patient_id: patientId, consultation_type: "physician_review",
        priority: "routine", duration_minutes: 30,
        source_module: "manual", notes: "Initiated from Clinical Operations workspace.",
      });
      toast.success("Teleconsultation created");
      navigate(`/teleconsult/room/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="clinical-dashboard">
      <PageHeader title={titleMap[mode]} subtitle={subtitleMap[mode]} />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Kpi label="Total" value={kpis.total} accent="#0B7E8C" icon={Users} testId="cl-kpi-total" />
        <Kpi label="High Risk" value={kpis.high_risk} accent="#EF4444" icon={ShieldAlert} testId="cl-kpi-high-risk" />
        <Kpi label="With Alerts" value={kpis.with_alerts} accent="#F59E0B" icon={BellRing} />
        <Kpi label="RPM Enrolled" value={kpis.rpm_enrolled} accent="#8B5CF6" icon={Activity} />
        <Kpi label="Abnormal Now" value={kpis.abnormal} accent="#0B7E8C" icon={AlertCircle} />
      </div>

      <div className="hah-card p-3 mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#94A3B8]" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setParams({ q: e.target.value }, { replace: true }); }}
            placeholder="Search name, MRN, phone…" className="pl-9" data-testid="cl-search" />
        </div>
        <Select value={facilityId} onValueChange={setFacilityId}>
          <SelectTrigger className="w-56" data-testid="cl-facility-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All facilities</SelectItem>
            {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="hah-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="text-left p-2.5">Patient</th>
              <th className="text-left p-2.5">Contact</th>
              <th className="text-left p-2.5">Facility</th>
              <th className="text-left p-2.5">Diagnosis</th>
              <th className="text-left p-2.5">Risk</th>
              <th className="text-left p-2.5">Abnormal Reading</th>
              <th className="text-right p-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-sm text-[#6B7280]">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-sm text-[#6B7280]">No active patients match your filters.</td></tr>}
            {items.map((c) => (
              <tr key={c.patient_id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]" data-testid={`cl-row-${c.patient_id}`}>
                <td className="p-2.5">
                  <button onClick={() => navigate(`/clinical/patients/${c.patient_id}`)} className="text-left hover:text-[#0B7E8C]" data-testid={`cl-open-${c.patient_id}`}>
                    <div className="font-semibold text-[#1F2937]">{c.name}</div>
                    <div className="text-xs text-[#6B7280] font-mono">{c.mrn} · {c.age || "—"}y · {c.gender || "—"}</div>
                  </button>
                </td>
                <td className="p-2.5 text-[#6B7280]">{c.phone || "—"}</td>
                <td className="p-2.5 text-[#6B7280]">{c.facility_name || "—"}</td>
                <td className="p-2.5 text-[#6B7280] max-w-[200px] truncate">{c.primary_diagnosis || "—"}</td>
                <td className="p-2.5"><RiskBadge ai={c.ai_risk} news2={c.news2_score} /></td>
                <td className="p-2.5"><AbnormalChip abn={c.abnormal_reading} /></td>
                <td className="p-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    {c.rpm_enrolled && (
                      <Button size="sm" variant="outline" onClick={() => navigate(`/rpm/patients/${c.patient_id}`)}
                        className="h-7 text-xs border-[#E5E7EB]" data-testid={`cl-rpm-${c.patient_id}`}>
                        <Activity className="w-3 h-3 mr-1" />RPM
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => startTele(c.patient_id)}
                      className="h-7 text-xs border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid={`cl-tele-${c.patient_id}`}>
                      <Video className="w-3 h-3 mr-1" />Tele
                    </Button>
                    <Button size="sm" onClick={() => navigate(`/clinical/patients/${c.patient_id}`)}
                      className="h-7 text-xs bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid={`cl-profile-${c.patient_id}`}>
                      Open <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
